import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { motion } from 'framer-motion';
import { DashboardLayout } from '@/layouts/DashboardLayout';

interface ProtectedRouteProps {
  requiredRole?: string;
  requiredRoles?: string[];
  requiredPermission?: string;
  redirectTo?: string;
}

export function ProtectedRoute({
  requiredRole,
  requiredRoles,
  requiredPermission,
  redirectTo = '/login'
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { userRole, isAdmin, isManager, hasPermission } = usePermissions();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-black">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-2 border-black/10 dark:border-white/10"></div>
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-black dark:border-white border-t-transparent"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-sm text-black/40 dark:text-white/40 font-medium">
            Verificando autenticação...
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check role requirement using normalized userRole
  const allowedRoles = requiredRoles || (requiredRole ? [requiredRole] : []);
  if (allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some(role => {
      const normalizedRequired = role.toLowerCase();
      // Admin has access to everything
      if (isAdmin()) return true;
      // Manager has access to manager and user routes
      if (normalizedRequired === 'manager' && isManager()) return true;
      // Direct role match
      return userRole === normalizedRequired;
    });

    if (!hasRequiredRole) {
      console.warn('🔒 Acesso negado:', {
        userRole,
        requiredRoles: allowedRoles,
        isAdmin: isAdmin(),
        isManager: isManager(),
      });

      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black text-black dark:text-white">
          <div className="text-center p-8 max-w-md">
            <div className="text-6xl mb-6">🔒</div>
            <h1 className="text-4xl font-bold mb-4">Acesso Negado</h1>
            <p className="text-black/60 dark:text-white/60 mb-6">
              Você não tem permissão para acessar esta página.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-medium hover:bg-black/90 dark:hover:bg-white/90 transition-colors"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
  }

  // Check permission requirement using hasPermission from usePermissions
  if (requiredPermission) {
    const hasRequiredPermission = hasPermission(requiredPermission as any);

    if (!hasRequiredPermission) {
      console.warn('🔒 Permissão negada:', {
        userRole,
        requiredPermission,
        userPermissions: user?.permissions,
      });

      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black text-black dark:text-white">
          <div className="text-center p-8 max-w-md">
            <div className="text-6xl mb-6">🔒</div>
            <h1 className="text-4xl font-bold mb-4">Acesso Negado</h1>
            <p className="text-black/60 dark:text-white/60 mb-6">
              Você não tem permissão para acessar esta página.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-medium hover:bg-black/90 dark:hover:bg-white/90 transition-colors"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
  }

  // User is authenticated and has required permissions
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}